// Theme A/B test: assign each visitor a sticky theme variant and report it so
// the backend can tie it to the acquisition funnel (see marketing.funnel_report
// `by_theme`). Mirrors attribution.ts: a value chosen once on the first visit,
// kept in localStorage, and sent on the balance call.

import { THEME_VARIANTS } from './theme';

const STORAGE_KEY = 'ra_theme'; // the randomly-assigned A/B bucket (immutable)
const PREF_KEY = 'ra_theme_pref'; // the user's manual override (display-only)
const CONTROL = 'matrix';

// The assigned A/B bucket for this browser. Chosen at random on first ever load
// and kept forever after — this is what we report to the funnel, so it is never
// changed by the user's manual theme choice (keeps the experiment honest).
// Falls back to the control if localStorage is unavailable (private mode).
export function getThemeVariant(): string {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && THEME_VARIANTS.includes(saved)) return saved;
    const pick = THEME_VARIANTS[Math.floor(Math.random() * THEME_VARIANTS.length)];
    localStorage.setItem(STORAGE_KEY, pick);
    return pick;
  } catch {
    return CONTROL;
  }
}

// The variant to actually DISPLAY: the user's manual choice if they made one,
// otherwise their assigned bucket. The manual choice never touches STORAGE_KEY,
// so the funnel still buckets by the originally-assigned theme.
export function getDisplayVariant(): string {
  try {
    const pref = localStorage.getItem(PREF_KEY);
    if (pref && THEME_VARIANTS.includes(pref)) return pref;
  } catch {
    /* ignore */
  }
  return getThemeVariant();
}

// Record a manual theme choice (display-only). Returns the value applied.
export function setThemePreference(variant: string): string {
  if (!THEME_VARIANTS.includes(variant)) return getDisplayVariant();
  try {
    localStorage.setItem(PREF_KEY, variant);
  } catch {
    /* ignore */
  }
  return variant;
}

// Append `theme_variant` to an existing query string (e.g. the attribution
// query), handling the '?' vs '&' separator, so one balance call carries both.
export function experimentQuery(existing = ''): string {
  const sep = existing ? '&' : '?';
  return `${existing}${sep}theme_variant=${encodeURIComponent(getThemeVariant())}`;
}
